import { useEffect, useMemo, useRef } from "react";
import { parseArticleBody, type ArticleInline } from "@care-by-ella/contracts";
import { useEditor, EditorContent, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";

// A constrained WYSIWYG for article bodies. Coaches don't know markdown, so they
// edit rich text here — but the value is still stored/served as markdown, and
// the three hand-rolled renderers (mobile Markdown.tsx, admin MarkdownPreview,
// web) only understand a small subset. So the editor is locked to exactly that
// subset — ATX headings (#/##/###), **bold**, "- " bullets, and "- [ ]/[x]"
// checklists — and we own BOTH conversions (markdown⇆doc) for that subset rather
// than trusting a generic markdown lib. (A generic parser mis-parsed a bullet
// list followed by a checklist into a phantom empty task item.)

// --- doc → markdown (the supported subset only) ----------------------------

type Node = JSONContent;

function serializeInline(nodes: Node[] | undefined): string {
  if (!nodes) return "";
  return nodes
    .map((n) => {
      if (n.type !== "text") return "";
      const bold = (n.marks ?? []).some((m) => m.type === "bold");
      const text = n.text ?? "";
      return bold && text ? `**${text}**` : text;
    })
    .join("");
}

// A list item / task item wraps a single paragraph — pull its inline content.
function itemInline(item: Node): string {
  const para = (item.content ?? []).find((c) => c.type === "paragraph");
  return serializeInline(para?.content);
}

function serializeBlock(node: Node): string | null {
  switch (node.type) {
    case "heading": {
      const level = Math.min(Math.max(node.attrs?.level ?? 1, 1), 3);
      return `${"#".repeat(level)} ${serializeInline(node.content)}`;
    }
    case "paragraph":
      return serializeInline(node.content);
    case "bulletList": {
      // Drop empty rows — an empty '- ' / '- [ ]' round-trips as literal text.
      const items = (node.content ?? [])
        .map(itemInline)
        .filter((t) => t.trim() !== "");
      return items.length ? items.map((t) => `- ${t}`).join("\n") : null;
    }
    case "taskList": {
      const items = (node.content ?? [])
        .map((ti) => ({ checked: !!ti.attrs?.checked, text: itemInline(ti) }))
        .filter((it) => it.text.trim() !== "");
      return items.length
        ? items
            .map((it) => `- [${it.checked ? "x" : " "}] ${it.text}`)
            .join("\n")
        : null;
    }
    default:
      return null;
  }
}

function docToMarkdown(doc: JSONContent): string {
  return (doc.content ?? [])
    .map(serializeBlock)
    .filter((b): b is string => b !== null)
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// --- markdown → HTML (parse for load) --------------------------------------
// TES-277: the grammar comes from the shared parseArticleBody in
// @care-by-ella/contracts — the same parser mobile renders with, so what
// loads into the editor is BY CONSTRUCTION what the app will render. This
// file keeps only the block-tree → Tiptap-HTML conversion (escaping lives
// here, not in the parser). The shared parser groups contiguous same-kind
// list runs, which preserves the phantom-empty-task-item fix (a bullet list
// next to a checklist stays two separate lists).

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function inlineToHtml(content: ArticleInline[]): string {
  return content
    .map((seg) =>
      seg.bold
        ? `<strong>${escapeHtml(seg.text)}</strong>`
        : escapeHtml(seg.text),
    )
    .join("");
}

function markdownToHtml(md: string): string {
  const html = parseArticleBody(md ?? "")
    .map((block) => {
      switch (block.kind) {
        case "heading":
          return `<h${block.level}>${inlineToHtml(block.content)}</h${block.level}>`;
        case "paragraph":
          // Soft-wrapped lines join with a space (mobile renders them the
          // same way).
          return `<p>${block.lines.map(inlineToHtml).join(" ")}</p>`;
        case "bullets":
          return `<ul>${block.items
            .map((item) => `<li><p>${inlineToHtml(item)}</p></li>`)
            .join("")}</ul>`;
        case "checklist":
          return `<ul data-type="taskList">${block.items
            .map(
              (item) =>
                `<li data-type="taskItem" data-checked="${item.checked}"><p>${inlineToHtml(item.content)}</p></li>`,
            )
            .join("")}</ul>`;
        case "cta":
          // Legacy web convention — never parsed without the legacy flag,
          // but keep the content visible if it ever appears.
          return `<p><strong>${escapeHtml(`${block.label} →`)}</strong></p>`;
        case "image":
          // The WYSIWYG can't author images; round-trip the reference as its
          // literal markdown line so an edit-and-save never drops it.
          return `<p>${escapeHtml(`![${block.alt}](media:${block.mediaKey})`)}</p>`;
      }
    })
    .join("");
  return html || "<p></p>";
}

// --- toolbar ---------------------------------------------------------------

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep the editor selection
      onClick={onClick}
      title={title}
      className={`min-w-[32px] h-8 px-sm rounded-md text-sm font-medium border ${
        active
          ? "bg-brand text-white border-brand"
          : "bg-surface text-textSecondary border-surfaceMuted hover:text-textPrimary"
      }`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (markdown: string) => void;
}) {
  // Tracks the last markdown reconciled with the editor, so echoing our own edit
  // back via `value` doesn't reset the editor (dropping the cursor mid-typing).
  // Seeded with the initial value so the mount `content` is the single parse
  // (the sync effect below then no-ops on first run).
  const reconciled = useRef<string | null>(value);
  // Parsed once for the mount content; later value changes go through the sync
  // effect below (so this doesn't re-parse the whole body on every keystroke).
  const initialContent = useMemo(() => markdownToHtml(value), []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Everything the mobile renderer can't display is disabled so it can't
        // be authored (or pasted) into the body.
        italic: false,
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        orderedList: false,
        link: false,
        underline: false,
      }),
      TaskList,
      TaskItem.configure({ nested: false }),
    ],
    // Existing markdown bodies are parsed to HTML by our own markdownToHtml and
    // fed to Tiptap as HTML; serialising back is docToMarkdown. Both stay inside
    // the app-supported subset.
    content: initialContent,
    onUpdate: ({ editor }) => {
      const md = docToMarkdown(editor.getJSON());
      reconciled.current = md;
      onChange(md);
    },
    editorProps: {
      attributes: {
        class:
          "prose-editor min-h-[220px] max-h-[520px] overflow-y-auto border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand",
      },
    },
  });

  // Pull external value changes (server load, locale switch) into the editor,
  // but skip echoes of our own edits.
  useEffect(() => {
    if (!editor) return;
    if (value === reconciled.current) return;
    editor.commands.setContent(markdownToHtml(value), { emitUpdate: false });
    reconciled.current = value;
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex flex-wrap items-center gap-xs">
        <ToolbarButton
          title="Fet"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <span className="font-bold">B</span>
        </ToolbarButton>
        {([1, 2, 3] as const).map((level) => (
          <ToolbarButton
            key={level}
            title={`Rubrik ${level}`}
            active={editor.isActive("heading", { level })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level }).run()
            }
          >
            H{level}
          </ToolbarButton>
        ))}
        <ToolbarButton
          title="Punktlista"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • Lista
        </ToolbarButton>
        <ToolbarButton
          title="Checklista"
          active={editor.isActive("taskList")}
          onClick={() => editor.chain().focus().toggleTaskList().run()}
        >
          ☑ Checklista
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
